import { ValidationError } from '~/errors';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type {
  CreateSubsidizerDto,
  SubsidizerDto,
  SubsidizerItemDto,
  SubsidizerPaginationDto,
  UpdateSubsidizerDto,
} from '~/swagger/portal/Api';
import type { ApiFilter } from '~/types/apiFilters';

const SUBSIDIZERS_URL = '/api/subsidizers';

export function validateSubsidizerId(id: string): void {
  if (!id) throw new ValidationError('Subsidizer ID must be provided');
}

export function validateCreateSubsidizer(subsidizer: CreateSubsidizerDto): void {
  if (!subsidizer) throw new ValidationError('Subsidizer object must be provided');
}

export function validateUpdateSubsidizer(subsidizer: UpdateSubsidizerDto): void {
  if (!subsidizer) throw new ValidationError('Subsidizer object must be provided');
}

export type SubsidizersFilter = ApiFilter<{
  type?: string;
}>;

export class SubsidizersRepository extends BaseRestRepository {
  public async getSubsidizers(filter?: SubsidizersFilter): Promise<SubsidizerPaginationDto> {
    const url = urlWithQuery(SUBSIDIZERS_URL, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getSubsidizersBatch(subsidizerIds?: string[]): Promise<SubsidizerItemDto[]> {
    const url = urlWithQuery(`${SUBSIDIZERS_URL}/batch`, subsidizerIds ? { subsidizerIds } : {});
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getSubsidizer(params: { id: string }): Promise<SubsidizerDto> {
    validateSubsidizerId(params.id);
    const url = `${SUBSIDIZERS_URL}/${params.id}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async createSubsidizer(payload: CreateSubsidizerDto): Promise<SubsidizerDto> {
    validateCreateSubsidizer(payload);
    const url = SUBSIDIZERS_URL;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async updateSubsidizer(params: { id: string }, payload: UpdateSubsidizerDto): Promise<SubsidizerDto> {
    validateSubsidizerId(params.id);
    validateUpdateSubsidizer(payload);
    const url = `${SUBSIDIZERS_URL}/${params.id}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async deleteSubsidizer(params: { id: string }): Promise<void> {
    validateSubsidizerId(params.id);
    const url = `${SUBSIDIZERS_URL}/${params.id}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
  }

  public async deactivateSubsidizer(params: { id: string }): Promise<SubsidizerDto> {
    validateSubsidizerId(params.id);
    const url = `${SUBSIDIZERS_URL}/${params.id}/deactivate`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
    });
    return this.getJsonResponse(response);
  }

  public async reactivateSubsidizer(params: { id: string }): Promise<SubsidizerDto> {
    validateSubsidizerId(params.id);
    const url = `${SUBSIDIZERS_URL}/${params.id}/reactivate`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
    });
    return this.getJsonResponse(response);
  }
}

export const subsidizersRepository = new SubsidizersRepository();
